/**
 * AccessSidebar — premium, RTL-first admin sidebar.
 *
 * Layout (top → bottom):
 *   1. Brand row             — logo + product name
 *   2. User card             — avatar + name + email + status chips
 *   3. Search box            — fuzzy filter across all visible menus
 *   4. Scrollable menu       — sectioned, with high-level section headers
 *      ("نمای کلی" / "صف‌های عملیاتی" / "محتوا و کاتالوگ" / ...)
 *   5. Logout button         — sticky footer
 *
 * RTL-correct visual details:
 *   - Icons render on the RIGHT side of each row (where the user's eye lands first in RTL)
 *   - Chevron on the LEFT (the "expand toward content" direction in RTL)
 *   - Active state: 3px accent bar on the RIGHT edge + tinted background
 *   - Group expansion: rotate chevron 90deg, sub-items indent on the RIGHT
 *
 * Data sources:
 *   - `useAccess()` → permissions, user, shift, grants
 *   - `adminMenus.ts` → menu tree + section taxonomy
 */

import React, { useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  Box,
  List,
  ListItemButton,
  Collapse,
  Typography,
  CircularProgress,
  Chip,
  Avatar,
  Divider,
  Tooltip,
  alpha,
} from '@mui/material';
import * as MuiIcons from '@mui/icons-material';
import {
  ADMIN_MENUS,
  SIDEBAR_SECTIONS,
  getMenuSection,
  isMenuGroup,
  isLeafVisible,
  type MenuEntry,
  type MenuLeaf,
  type MenuGroup,
  type SidebarSection,
} from '../../constants/adminMenus';
import { useAccess } from '../../hooks/useAccess';
import { useAuth } from '../../context/AuthContext';

interface AccessSidebarProps {
  onItemClick?: () => void;
}

/* ---------- Design tokens ---------- */
const BG_TOP = '#2A1520';            // deep plum (CasmaShop rose family)
const BG_BOTTOM = '#3A1F2C';         // plum → wine
const TEXT_PRIMARY = '#F8E9EE';      // blush white (14.6:1 on BG_TOP)
const TEXT_MUTED = '#C9A9B6';        // muted rose-grey (7:1+)
const TEXT_DIMMED = '#A88894';       // dimmed rose-grey (4.7:1+)
const ACCENT = '#E07F98';            // CasmaShop rose (5.4:1+ on plum)
const ACCENT_DEEP = '#C25E7E';       // rose dark
const ACCENT_SOFT = alpha(ACCENT, 0.18);
const HOVER_BG = alpha('#ffffff', 0.05);
const ACTIVE_BG = alpha(ACCENT, 0.14);
const DIVIDER = alpha('#ffffff', 0.06);

/* ---------- Icon resolver ---------- */
function Icon({ name, size = 18, color }: { name: string; size?: number; color?: string }) {
  const Comp = (MuiIcons as Record<string, React.ComponentType<any>>)[name];
  const I = Comp || MuiIcons.Circle;
  return <I sx={{ fontSize: size, color }} />;
}

const AccessSidebar: React.FC<AccessSidebarProps> = ({ onItemClick }) => {
  const location = useLocation();
  const { has, isLoading, user, shift, activeGrants } = useAccess();
  const auth = useAuth();
  const [openGroups, setOpenGroups] = useState<Set<string>>(new Set());
  const [query, setQuery] = useState('');

  /* ----- Filter visible menus by permission + auto-detect active group ----- */
  const { visibleMenus, autoOpenGroup } = useMemo(() => {
    const out: MenuEntry[] = [];
    let active: string | null = null;
    for (const e of ADMIN_MENUS) {
      if (isMenuGroup(e)) {
        const visibleChildren = e.children.filter((c) => isLeafVisible(c, has));
        if (!visibleChildren.length) continue;
        if (
          visibleChildren.some(
            (c) => c.path === location.pathname || location.pathname.startsWith(c.path + '/'),
          )
        ) {
          active = e.id;
        }
        out.push({ ...e, children: visibleChildren });
      } else if (isLeafVisible(e, has)) {
        out.push(e);
      }
    }
    return { visibleMenus: out, autoOpenGroup: active };
  }, [has, location.pathname]);

  /* ----- Optional search filter (drops items + auto-expands matches) ----- */
  const filteredMenus = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return visibleMenus;
    return visibleMenus
      .map((e): MenuEntry | null => {
        if (isMenuGroup(e)) {
          const matchedChildren = e.children.filter(
            (c) => c.label.toLowerCase().includes(q) || c.path.toLowerCase().includes(q),
          );
          if (e.label.toLowerCase().includes(q)) return e; // group title match
          if (matchedChildren.length) return { ...e, children: matchedChildren };
          return null;
        }
        return e.label.toLowerCase().includes(q) || e.path.toLowerCase().includes(q) ? e : null;
      })
      .filter((x): x is MenuEntry => x !== null);
  }, [query, visibleMenus]);

  /* ----- Auto-open the group containing the active route ----- */
  useEffect(() => {
    if (autoOpenGroup) {
      setOpenGroups((prev) => {
        if (prev.has(autoOpenGroup)) return prev;
        const next = new Set(prev);
        next.add(autoOpenGroup);
        return next;
      });
    }
  }, [autoOpenGroup]);

  /* ----- Auto-expand every group when searching ----- */
  const effectiveOpenGroups = useMemo(() => {
    if (query.trim()) {
      return new Set(filteredMenus.filter(isMenuGroup).map((g) => g.id));
    }
    return openGroups;
  }, [query, filteredMenus, openGroups]);

  const toggleGroup = (id: string) => {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const isLeafActive = (path: string) =>
    location.pathname === path ||
    (path !== '/' && location.pathname.startsWith(path + '/'));

  /* ----- Bucket entries by section for visual headers ----- */
  const sectionsToRender = useMemo(() => {
    const buckets = new Map<SidebarSection, MenuEntry[]>();
    for (const e of filteredMenus) {
      const id = (e as MenuGroup).id || (e as MenuLeaf).id;
      const sec = getMenuSection(id);
      const arr = buckets.get(sec);
      if (arr) arr.push(e);
      else buckets.set(sec, [e]);
    }
    return SIDEBAR_SECTIONS.map((s) => ({ section: s, items: buckets.get(s.id) ?? [] })).filter(
      (b) => b.items.length > 0,
    );
  }, [filteredMenus]);

  /* ----- LEAF item ----- */
  const renderLeaf = (leaf: MenuLeaf, nested = false) => {
    const active = isLeafActive(leaf.path);
    return (
      <Tooltip key={leaf.id} title={leaf.label} placement="left" enterDelay={500} arrow>
        <ListItemButton
          component={NavLink as any}
          to={leaf.path}
          onClick={onItemClick}
          sx={{
            mx: 1.25,
            my: 0.25,
            borderRadius: 1.5,
            minHeight: nested ? 36 : 40,
            pr: 1.5,
            pl: 1.5,
            position: 'relative',
            color: active ? '#fff' : TEXT_PRIMARY,
            bgcolor: active ? ACTIVE_BG : 'transparent',
            transition: 'background 0.18s ease, color 0.18s ease, transform 0.18s ease',
            '&:hover': {
              bgcolor: active ? ACTIVE_BG : HOVER_BG,
              transform: 'translateX(-2px)',
              color: '#fff',
            },
            '&::after': active
              ? {
                  content: '""',
                  position: 'absolute',
                  right: -1,
                  top: '20%',
                  bottom: '20%',
                  width: 3,
                  bgcolor: ACCENT,
                  borderRadius: '3px 0 0 3px',
                  boxShadow: `0 0 8px ${alpha(ACCENT, 0.6)}`,
                }
              : undefined,
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: nested ? 1 : 1.25,
          }}
        >
          {/* Icon on RIGHT in RTL — flex-row places it first when dir=rtl */}
          <Box
            sx={{
              minWidth: 24,
              display: 'flex',
              justifyContent: 'center',
              color: active ? ACCENT : TEXT_MUTED,
              flexShrink: 0,
            }}
          >
            <Icon name={leaf.icon} size={nested ? 16 : 18} />
          </Box>
          {/* Nested item indent indicator */}
          {nested && (
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                bgcolor: active ? ACCENT : alpha(TEXT_MUTED, 0.4),
                flexShrink: 0,
              }}
            />
          )}
          <Typography
            sx={{
              flex: 1,
              fontSize: nested ? 12.5 : 13.5,
              fontWeight: active ? 600 : 500,
              lineHeight: 1.4,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              color: 'inherit',
            }}
          >
            {leaf.label}
          </Typography>
        </ListItemButton>
      </Tooltip>
    );
  };

  /* ----- GROUP item ----- */
  const renderGroup = (group: MenuGroup) => {
    const isOpen = effectiveOpenGroups.has(group.id);
    const groupHasActive = group.children.some((c) => isLeafActive(c.path));
    return (
      <Box key={group.id} sx={{ mb: 0.25 }}>
        <Tooltip title={group.label} placement="left" enterDelay={500} arrow>
          <ListItemButton
            onClick={() => toggleGroup(group.id)}
            sx={{
              mx: 1.25,
              my: 0.25,
              borderRadius: 1.5,
              minHeight: 42,
              pr: 1.5,
              pl: 1.5,
              color: groupHasActive ? '#fff' : TEXT_PRIMARY,
              bgcolor: groupHasActive && !isOpen ? alpha(ACCENT, 0.08) : 'transparent',
              transition: 'all 0.18s ease',
              '&:hover': { bgcolor: HOVER_BG, color: '#fff' },
              display: 'flex',
              alignItems: 'center',
              gap: 1.25,
            }}
          >
            <Box
              sx={{
                minWidth: 24,
                display: 'flex',
                justifyContent: 'center',
                color: groupHasActive ? ACCENT : TEXT_MUTED,
                flexShrink: 0,
              }}
            >
              <Icon name={group.icon} size={19} />
            </Box>
            <Typography
              sx={{
                flex: 1,
                fontSize: 13.5,
                fontWeight: groupHasActive ? 700 : 600,
                color: 'inherit',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {group.label}
            </Typography>
            <Chip
              size="small"
              label={group.children.length}
              sx={{
                height: 18,
                fontSize: 10,
                fontWeight: 600,
                bgcolor: groupHasActive ? ACCENT_SOFT : alpha('#fff', 0.06),
                color: groupHasActive ? ACCENT : TEXT_MUTED,
                '& .MuiChip-label': { px: 0.75 },
              }}
            />
            <MuiIcons.ChevronLeft
              sx={{
                fontSize: 18,
                color: TEXT_MUTED,
                transition: 'transform 0.22s ease',
                transform: isOpen ? 'rotate(-90deg)' : 'rotate(0deg)',
              }}
            />
          </ListItemButton>
        </Tooltip>
        <Collapse in={isOpen} timeout={220} unmountOnExit>
          <Box
            sx={{
              position: 'relative',
              ml: 2.5,
              mr: 2.5,
              pl: 1.25,
              borderRight: `1px dashed ${alpha(ACCENT, 0.25)}`,
            }}
          >
            <List dense disablePadding>
              {group.children.map((c) => renderLeaf(c, true))}
            </List>
          </Box>
        </Collapse>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        height: '100vh',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: `linear-gradient(180deg, ${BG_TOP} 0%, ${BG_BOTTOM} 100%)`,
        color: TEXT_PRIMARY,
        borderLeft: `1px solid ${DIVIDER}`,
        boxShadow: '0 0 24px rgba(0,0,0,0.35)',
        fontFamily: 'Vazirmatn, IRANSansX, IRANSans, Tahoma, sans-serif',
        position: 'relative',
        '&::before': {
          // subtle accent halo at the top — explicit % positions are
          // safer than "top right" with the stylis RTL plugin
          content: '""',
          position: 'absolute',
          top: 0,
          right: 0,
          left: 0,
          height: 140,
          background: `radial-gradient(circle at 100% 0%, ${alpha(ACCENT, 0.18)} 0%, transparent 70%)`,
          pointerEvents: 'none',
        },
      }}
    >
      {/* ========= BRAND ROW ========= */}
      <Box
        sx={{
          px: 2.25,
          pt: 2.25,
          pb: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          position: 'relative',
        }}
      >
        <Box
          sx={{
            width: 38,
            height: 38,
            borderRadius: 1.5,
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: `0 6px 18px ${alpha(ACCENT, 0.4)}`,
            flexShrink: 0,
          }}
        >
          <Box component="img" src="/favicon.svg" alt="کازماشاپ" sx={{ width: 38, height: 38, display: "block" }} />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            sx={{ color: '#fff', fontSize: 15, fontWeight: 700, lineHeight: 1.1 }}
          >
            کازماشاپ
          </Typography>
          <Typography
            sx={{ color: TEXT_MUTED, fontSize: 10.5, fontWeight: 500, letterSpacing: 0.5 }}
          >
            پنل مدیریت · CasmaShop
          </Typography>
        </Box>
      </Box>

      {/* ========= USER CARD ========= */}
      <Box
        sx={{
          mx: 1.5,
          mb: 1.5,
          p: 1.5,
          borderRadius: 2,
          background: alpha('#fff', 0.04),
          border: `1px solid ${alpha('#fff', 0.06)}`,
          position: 'relative',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.25 }}>
          <Avatar
            sx={{
              bgcolor: ACCENT_SOFT,
              color: ACCENT,
              width: 42,
              height: 42,
              fontWeight: 700,
              fontSize: 16,
              border: `2px solid ${alpha(ACCENT, 0.4)}`,
            }}
          >
            {user.name?.[0] || user.email?.[0]?.toUpperCase() || 'A'}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{ color: '#fff', fontSize: 13, fontWeight: 700, lineHeight: 1.2 }}
              noWrap
            >
              {user.name || 'ادمین'}
            </Typography>
            <Typography
              sx={{ color: TEXT_MUTED, fontSize: 11, lineHeight: 1.4, mt: 0.25 }}
              noWrap
            >
              {user.email || '—'}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {user.isSuperAdmin && (
            <Tooltip title="دسترسی کامل به همه بخش‌ها">
              <Chip
                size="small"
                icon={<MuiIcons.WorkspacePremium sx={{ fontSize: 12 }} />}
                label="سوپر-ادمین"
                sx={{
                  height: 22,
                  fontSize: 10,
                  fontWeight: 600,
                  bgcolor: alpha('#fbbf24', 0.16),
                  color: '#fcd34d',
                  borderColor: alpha('#fbbf24', 0.4),
                  border: '1px solid',
                  '& .MuiChip-icon': { color: '#fcd34d', mr: '-3px', ml: '6px' },
                }}
              />
            </Tooltip>
          )}
          <Chip
            size="small"
            icon={
              <MuiIcons.Circle
                sx={{
                  fontSize: 8,
                  color: shift.status === 'on_shift' ? '#22c55e' : TEXT_MUTED,
                }}
              />
            }
            label={shift.status === 'on_shift' ? 'در شیفت' : 'خارج شیفت'}
            sx={{
              height: 22,
              fontSize: 10,
              fontWeight: 600,
              bgcolor:
                shift.status === 'on_shift'
                  ? alpha('#22c55e', 0.16)
                  : alpha(TEXT_MUTED, 0.12),
              color: shift.status === 'on_shift' ? '#4ade80' : TEXT_MUTED,
              borderColor:
                shift.status === 'on_shift' ? alpha('#22c55e', 0.4) : alpha(TEXT_MUTED, 0.3),
              border: '1px solid',
              '& .MuiChip-icon': { mr: '-3px', ml: '6px' },
            }}
          />
          {activeGrants.length > 0 && (
            <Tooltip title="استثناهای دسترسی موقت فعال">
              <Chip
                size="small"
                icon={<MuiIcons.LockOpen sx={{ fontSize: 12 }} />}
                label={`${activeGrants.length} استثنا`}
                sx={{
                  height: 22,
                  fontSize: 10,
                  fontWeight: 600,
                  bgcolor: ACCENT_SOFT,
                  color: ACCENT,
                  borderColor: alpha(ACCENT, 0.4),
                  border: '1px solid',
                  '& .MuiChip-icon': { color: ACCENT, mr: '-3px', ml: '6px' },
                }}
              />
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* ========= SEARCH (plain input to avoid emotion/stylis RTL plugin
          choking on InputBase's `& input::placeholder` nested selector) ===== */}
      <Box sx={{ mx: 1.5, mb: 1 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            px: 1.5,
            py: 0.75,
            borderRadius: 1.5,
            bgcolor: alpha('#fff', 0.05),
            border: `1px solid ${alpha('#fff', 0.06)}`,
            transition: 'border-color 0.18s ease, background 0.18s ease',
            '&:focus-within': {
              borderColor: alpha(ACCENT, 0.5),
              bgcolor: alpha('#fff', 0.07),
            },
          }}
        >
          <MuiIcons.Search sx={{ fontSize: 16, color: TEXT_MUTED }} />
          <input
            type="text"
            placeholder="جستجو در منوها..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="sidebar-search-input"
            style={{
              flex: 1,
              minWidth: 0,
              border: 'none',
              outline: 'none',
              background: 'transparent',
              color: '#fff',
              fontSize: 12.5,
              fontFamily: 'inherit',
              direction: 'rtl',
              padding: 0,
            }}
          />
          {query && (
            <MuiIcons.Close
              sx={{ fontSize: 14, color: TEXT_MUTED, cursor: 'pointer' }}
              onClick={() => setQuery('')}
            />
          )}
        </Box>
      </Box>

      <Divider sx={{ borderColor: DIVIDER }} />

      {/* ========= SCROLLABLE MENU ========= */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          py: 1,
          '&::-webkit-scrollbar': { width: 5 },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': {
            background: alpha('#fff', 0.08),
            borderRadius: 3,
          },
          '&::-webkit-scrollbar-thumb:hover': { background: alpha('#fff', 0.18) },
          scrollbarWidth: 'thin',
          scrollbarColor: `${alpha('#fff', 0.1)} transparent`,
        }}
      >
        {isLoading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <CircularProgress size={22} sx={{ color: ACCENT }} />
          </Box>
        ) : sectionsToRender.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <MuiIcons.SearchOff sx={{ fontSize: 36, color: TEXT_DIMMED, mb: 1 }} />
            <Typography
              sx={{ color: TEXT_MUTED, fontSize: 12.5, lineHeight: 1.7 }}
            >
              {query ? 'نتیجه‌ای یافت نشد.' : 'هیچ منویی برای شما تعریف نشده.'}
              <br />
              {!query && 'با مدیر تماس بگیرید.'}
            </Typography>
          </Box>
        ) : (
          sectionsToRender.map(({ section, items }) => (
            <Box key={section.id} sx={{ mb: 1 }}>
              <Box
                sx={{
                  px: 2.5,
                  pt: 1.5,
                  pb: 0.5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    height: '1px',
                    background: `linear-gradient(270deg, ${alpha('#fff', 0.08)}, transparent)`,
                  }}
                />
                <Typography
                  sx={{
                    fontSize: 9.5,
                    fontWeight: 700,
                    color: TEXT_DIMMED,
                    letterSpacing: 1.2,
                    textTransform: 'uppercase',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {section.label}
                </Typography>
              </Box>
              <List dense disablePadding>
                {items.map((e) =>
                  isMenuGroup(e) ? renderGroup(e) : renderLeaf(e as MenuLeaf),
                )}
              </List>
            </Box>
          ))
        )}
      </Box>

      <Divider sx={{ borderColor: DIVIDER }} />

      {/* ========= STICKY FOOTER ========= */}
      <Box sx={{ p: 1.25 }}>
        <ListItemButton
          onClick={() => auth.logout?.()}
          sx={{
            borderRadius: 1.5,
            color: '#fca5a5',
            minHeight: 40,
            gap: 1.25,
            transition: 'all 0.18s ease',
            '&:hover': {
              bgcolor: alpha('#ef4444', 0.15),
              color: '#fecaca',
            },
          }}
        >
          <Box
            sx={{
              minWidth: 24,
              display: 'flex',
              justifyContent: 'center',
              color: 'inherit',
            }}
          >
            <MuiIcons.Logout sx={{ fontSize: 18 }} />
          </Box>
          <Typography
            sx={{ flex: 1, fontSize: 13, fontWeight: 600, color: 'inherit' }}
          >
            خروج از حساب
          </Typography>
        </ListItemButton>
      </Box>
    </Box>
  );
};

export default AccessSidebar;
